import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const initial = { name: "", email: "", company: "", message: "", website: "" };

function PartnerInquiryForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.company || !form.message) {
      setError("Please fill in every field.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/partner-inquiry", form);
      setDone(true);
      toast.success(res.data?.message || "Thanks — we'll be in touch.");
      setForm(initial);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="border border-white/20 p-6 flex flex-col gap-3"
        data-testid="partner-inquiry-success"
      >
        <span className="eyebrow" style={{ color: "var(--bg)" }}>Received</span>
        <p className="font-display text-2xl md:text-3xl leading-[1.1] tracking-[-0.01em]">
          Thank you. The partnerships team will be in touch.
        </p>
        <button
          className="text-[12px] uppercase tracking-[0.18em] underline underline-offset-4 self-start hover:text-[var(--accent)] transition-colors"
          onClick={() => setDone(false)}
          data-testid="partner-inquiry-new"
        >
          Send another →
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6 section-dark"
      data-testid="partner-inquiry-form"
      noValidate
    >
      <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }} aria-hidden>
        <label>
          Website
          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={onChange} />
        </label>
      </div>

      <div className="field">
        <label htmlFor="pi-name">Name</label>
        <input id="pi-name" name="name" value={form.name} onChange={onChange} data-testid="pi-input-name" />
      </div>
      <div className="field">
        <label htmlFor="pi-email">Email</label>
        <input id="pi-email" name="email" type="email" value={form.email} onChange={onChange} data-testid="pi-input-email" />
      </div>
      <div className="field md:col-span-2">
        <label htmlFor="pi-company">Company</label>
        <input id="pi-company" name="company" value={form.company} onChange={onChange} data-testid="pi-input-company" />
      </div>
      <div className="field md:col-span-2">
        <label htmlFor="pi-message">How would you like to partner?</label>
        <textarea id="pi-message" name="message" rows={4} value={form.message} onChange={onChange} data-testid="pi-input-message" />
      </div>

      {error && (
        <p className="md:col-span-2 text-[13px] text-[var(--accent)]" data-testid="pi-error">
          {error}
        </p>
      )}

      <div className="md:col-span-2 flex items-center justify-between mt-2">
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/45">Confidential</p>
        <button
          type="submit"
          className="btn-accent btn-accent--sm"
          disabled={submitting}
          data-testid="pi-submit"
        >
          {submitting ? "Sending…" : "Send inquiry"}
        </button>
      </div>
    </form>
  );
}

export default function Footer({ settings }) {
  const year = new Date().getFullYear();

  return (
    <footer
      id="partner-inquiry"
      data-testid="site-footer"
      className="bg-[var(--dark)] text-[var(--bg)] pt-24 pb-10"
    >
      <div className="max-w-page container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 pb-16 border-b border-white/12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="section-index">08</span>
              <span className="eyebrow eyebrow--accent">Partner inquiry</span>
            </div>
            <h3 className="font-display text-3xl sm:text-4xl md:text-5xl mt-4 max-w-[16ch] leading-[1.02] tracking-[-0.01em]">
              Talk to us about partnering.
            </h3>
            <p className="text-[14px] leading-[1.55] mt-5 max-w-[42ch] text-white/60">
              A short note is enough — the partnerships team will reply
              personally within a few working days.
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <PartnerInquiryForm />
          </div>
        </div>

        <div className="pt-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-5">
            <span className="wordmark text-[14px] md:text-[16px]" style={{lineHeight: 1.02}}>
              <span>Norwegian</span>
              <span>AI Summit</span>
            </span>
            <p className="mt-4 text-[13px] text-white/60 max-w-[36ch]">
              {settings?.venue} · {settings?.date_label}
            </p>
          </div>

          <div className="md:col-span-4 md:col-start-6 flex flex-col gap-1 text-[13px] text-white/75">
            <span className="uppercase tracking-[0.2em] text-[11px] text-white/45 mb-1">
              Contact
            </span>
            <a href="mailto:hello@norwegianaisummit.no" className="link-underline" data-testid="footer-email">
              hello@norwegianaisummit.no
            </a>
            <a href="mailto:partners@norwegianaisummit.no" className="link-underline" data-testid="footer-partner-email">
              partners@norwegianaisummit.no
            </a>
          </div>

          <div className="md:col-span-3 md:col-start-10 md:text-right text-[12px] text-white/50">
            © {year} Norwegian AI Summit
            <br />
            All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}
